import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiShield, FiCheckCircle, FiLayers, FiTool, 
  FiAlertTriangle, FiHome, FiDollarSign, FiList, FiCheckSquare,
  FiThermometer, FiVolume2, FiDroplet, FiLayout, FiMaximize,
  FiGrid, FiChevronsRight, FiX, FiCheck, FiInfo, FiSearch
} = FiIcons;

function DrywallGuide() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Trockenbau Anleitung & Fehlervermeidung: Der Experten-Guide (2026)",
        "alternativeHeadline": "Wände ziehen & Decken abhängen: Was Bauherren wirklich beachten müssen",
        "image": "https://mx.ausbau.nrw/img/Trocken_und_Akustikbau_xl.jpg",
        "author": {
          "@type": "Person",
          "name": "Maxim Kelm",
          "jobTitle": "Geschäftsführer MK-AUSBAU",
          "url": "https://ausbau.nrw/ueber"
        },
        "publisher": {
          "@type": "Organization",
          "name": "MK-AUSBAU",
          "logo": {
            "@type": "ImageObject",
            "url": "https://ausbau.nrw/logo-icon.svg"
          }
        },
        "datePublished": "2026-03-05",
        "dateModified": "2026-03-25",
        "description": "Trockenbau richtig machen 🛠️ Anleitung für Wände & Decken, Profil-Erklärung (CW/UW) & Q1-Q4 Spachtelqualitäten. Vermeiden Sie diese 5 Fehler!",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://ausbau.nrw/blog/trockenbau-leitfaden"
        }
      },
      {
        "@type": "HowTo",
        "name": "Trockenbauwand erstellen in 6 Schritten",
        "description": "Fachgerechte Montage einer Ständerwand mit Metallunterkonstruktion und Beplankung.",
        "step": [
          {
            "@type": "HowToStep",
            "name": "Planung & Einmessen",
            "text": "Position der Wand am Boden und der Decke markieren (Schlagschnur/Laser)."
          },
          {
            "@type": "HowToStep",
            "name": "Profile vorbereiten",
            "text": "UW-Profile für Boden/Decke und CW-Profile für die Ständer zuschneiden. Dichtungsband aufkleben!"
          },
          {
            "@type": "HowToStep",
            "name": "Unterkonstruktion stellen",
            "text": "UW-Profile verdübeln. CW-Profile einstellen (nicht verschrauben, nur klemmen)."
          },
          {
            "@type": "HowToStep",
            "name": "Erste Seite beplanken",
            "text": "Gipskartonplatten anbringen. Beginnen Sie mit einer ganzen Platte."
          },
          {
            "@type": "HowToStep",
            "name": "Dämmung & Installation",
            "text": "Hohlraumdämmung (Mineralwolle) einlegen und Kabel ziehen."
          },
          {
            "@type": "HowToStep",
            "name": "Zweite Seite schließen",
            "text": "Wand schließen (Fugenversatz zur ersten Seite beachten!) und verspachteln."
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Was bedeuten die Profile CW, UW, UA, CD, UD?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Das sind die Standard-Metallprofile. 'U' sind waagerechte Boden/Decken-Profile, 'C' sind senkrechte Ständer. 'W' steht für Wand, 'D' für Decke. UA sind verstärkte Türpfosten-Profile."
            }
          },
          {
            "@type": "Question",
            "name": "Welche Spachtelqualität brauche ich?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Q1 reicht für Fliesen. Q2 ist Standard für Raufaser. Q3 für matte Anstriche/feine Tapeten. Q4 ist die Premium-Vollflächenspachtelung für Glanztechniken oder direktes Streichen."
            }
          }
        ]
      }
    ]
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Trockenbau Anleitung & Tipps 2026: Wände & Decken richtig bauen"
        description="Trockenbau planen & umsetzen: 🛠️ Anleitung für Wände & Decken, Profil-Kunde (CW/UW) & Q1-Q4 Spachteln. Vermeiden Sie Risse & Schallbrücken!"
        keywords="Trockenbau Anleitung, Trockenbauwand einziehen, Decke abhängen Anleitung, CW UW Profile Unterschied, Spachtelqualität Q1 Q2 Q3 Q4, Knauf Rigips verarbeiten, Trockenbau Heinsberg"
        schema={articleSchema}
        canonical="/blog/trockenbau-leitfaden"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg prose-mk max-w-none"
        >
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4">
              <SafeIcon icon={FiLayers} />
              <span>Innenausbau & Praxiswissen</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Was müssen Sie beim Trockenbau beachten? Der große Praxis-Guide für fehlerfreie Wände & Decken
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Ratgeber von Maxim Kelm</span>
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Aktualisiert: März 2026</span>
            </div>
          </header>

          <div className="bg-mk-teal-50 border-l-4 border-mk-teal-500 p-6 mb-12 rounded-r-xl shadow-sm">
            <h2 className="text-mk-teal-900 font-bold mt-0 mb-2 text-lg">Das Wichtigste in Kürze (TL;DR):</h2>
            <p className="text-mk-teal-800 m-0 text-base leading-relaxed">
              Erfolgreicher Trockenbau basiert auf drei Säulen: <strong>Statik</strong> (richtige Profile & Abstände), <strong>Bauphysik</strong> (Schallschutz & Feuchteschutz) und <strong>Oberflächengüte</strong> (Q1 bis Q4). <br/><br/>
              Die häufigsten Fehler sind fehlende Entkopplung (Schallbrücken), falsche Beplankung (Kreuzfugen) und die Verwendung von Standardplatten in Feuchträumen. Nutzen Sie immer Dichtungsband unter den Rahmenprofilen!
            </p>
          </div>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12">
            Trockenbau klingt einfach: Profile stellen, Platten dran, fertig. Doch der Teufel steckt im Detail. Risse in den Fugen, hellhörige Wände oder Schimmel im Bad sind oft die Folge von kleinen Montagefehlern. In diesem Guide erfahren Sie, wie Sie wie ein Profi planen.
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            1. Das Profil-ABC: Welches Metall wofür?
          </h2>
          <p className="text-gray-700 mb-6">
            Wer im Baumarkt steht, ist oft verwirrt. Hier die Übersetzung der Kürzel für Ihre Einkaufsliste. Suchmaschinen nutzen diese Struktur gerne für "Lexikon"-Snippets.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-bold text-mk-teal-600 mb-4 flex items-center">
                <SafeIcon icon={FiLayout} className="mr-2" />
                Wand-Profile (Ständerwerk)
              </h3>
              <ul className="space-y-3 m-0 p-0 list-none text-sm">
                <li className="flex flex-col">
                  <span className="font-bold text-gray-900">UW-Profil (U-Form, Wand)</span>
                  <span className="text-gray-600">Das "Schienen"-Profil für Boden und Decke. Hier werden die Ständer reingestellt.</span>
                </li>
                <li className="flex flex-col border-t border-gray-100 pt-2">
                  <span className="font-bold text-gray-900">CW-Profil (C-Form, Wand)</span>
                  <span className="text-gray-600">Der senkrechte Ständer. Trägt die Platten.</span>
                </li>
                <li className="flex flex-col border-t border-gray-100 pt-2">
                  <span className="font-bold text-gray-900">UA-Profil (Aussteifung)</span>
                  <span className="text-gray-600">Verstärktes Profil (dickes Blech) für Türöffnungen. Pflicht bei schweren Türen!</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-bold text-mk-teal-600 mb-4 flex items-center">
                <SafeIcon icon={FiMaximize} className="mr-2" />
                Decken-Profile (Abhängung)
              </h3>
              <ul className="space-y-3 m-0 p-0 list-none text-sm">
                <li className="flex flex-col">
                  <span className="font-bold text-gray-900">UD-Profil (U-Form, Decke)</span>
                  <span className="text-gray-600">Der Wandanschluss für die abgehängte Decke (umlaufend).</span>
                </li>
                <li className="flex flex-col border-t border-gray-100 pt-2">
                  <span className="font-bold text-gray-900">CD-Profil (C-Form, Decke)</span>
                  <span className="text-gray-600">Das Tragprofil, an das die Platten geschraubt werden.</span>
                </li>
              </ul>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            2. Qualitätsstufen Spachteln (Q1 bis Q4)
          </h2>
          <p className="text-gray-700 mb-6">
            "Wand glatt machen" ist kein technischer Begriff. Vereinbaren Sie mit Ihrem Handwerker immer eine Qualitätsstufe nach Merkblatt Nr. 2 des Bundesverbands der Gipsindustrie.
          </p>

          <div className="overflow-x-auto mb-12 rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full text-sm text-left text-gray-700">
              <thead className="text-xs text-white uppercase bg-gray-900">
                <tr>
                  <th className="px-6 py-4 w-20">Stufe</th>
                  <th className="px-6 py-4">Leistungsumfang</th>
                  <th className="px-6 py-4">Ideal geeignet für...</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                <tr>
                  <td className="px-6 py-4 font-bold bg-gray-50">Q1</td>
                  <td className="px-6 py-4">Grundverspachtelung (Fugen füllen + Schraubenköpfe)</td>
                  <td className="px-6 py-4">Fliesenlagen, grobe Beläge</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-bold bg-gray-50">Q2</td>
                  <td className="px-6 py-4">Standardverspachtelung (Q1 + Nachspachteln der Übergänge)</td>
                  <td className="px-6 py-4">Raufaser, grobe Strukturputze (Standard!)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-bold bg-gray-50">Q3</td>
                  <td className="px-6 py-4">Sonderverspachtelung (Q2 + breites Ausspachteln der Poren)</td>
                  <td className="px-6 py-4">Matte Anstriche, feine Tapeten</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-bold bg-mk-teal-50 text-mk-teal-700">Q4</td>
                  <td className="px-6 py-4 text-mk-teal-700 font-bold">Vollflächenspachtelung (Q3 + kompletter Überzug &gt;1mm)</td>
                  <td className="px-6 py-4 font-bold text-mk-teal-700">Glanztechniken, Seidentapeten, Streiflicht</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            3. Die 5 häufigsten Fehler (und wie Sie sie vermeiden)
          </h2>
          <div className="space-y-6 mb-12">
            {[
              { error: "Fehlendes Dichtungsband", consequence: "Schallbrücken & Risse", fix: "Trennwandband (Moosgummi) unter JEDES Randprofil kleben." },
              { error: "Kreuzfugen bei der Beplankung", consequence: "Instabilität & Risse", fix: "Platten immer im Versatz montieren (mind. 40 cm Fugenversatz)." },
              { error: "Schraubenabstand zu groß", consequence: "Platten hängen durch", fix: "Wand: max. 25 cm. Decke: max. 17 cm Abstand." },
              { error: "CW-Profile verschraubt", consequence: "Wand kann nicht 'arbeiten'", fix: "CW-Profile stehen lose im UW-Profil. Nur durch die Platte fixieren!" },
              { error: "Falsche Platte im Bad", consequence: "Schimmel & Aufquellen", fix: "Imprägnierte Platten (Grün) oder Zementfaserplatten nutzen." }
            ].map((item, i) => (
              <div key={i} className="flex bg-white border-l-4 border-mk-red-500 shadow-sm rounded-r-lg p-4">
                <div className="mr-4 text-mk-red-500 mt-1">
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 m-0 text-base">{item.error}</h4>
                  <p className="text-sm text-gray-600 mt-1 mb-2">Folge: {item.consequence}</p>
                  <div className="flex items-center text-sm font-bold text-mk-teal-600">
                    <SafeIcon icon={FiCheck} className="mr-1 w-4 h-4" />
                    Lösung: {item.fix}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Fazit: Planung spart Zeit und Nerven
          </h2>
          <div className="bg-gray-900 text-white p-10 rounded-[2.5rem] shadow-2xl mb-16 text-center">
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Trockenbau verzeiht viel, aber nicht alles. Wer bei der Unterkonstruktion schlampt (nicht im Lot, kein Dichtband), wird beim Spachteln verzweifeln. Nehmen Sie sich Zeit für das "Skelett" der Wand – die Platten sind dann ein Kinderspiel.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Entkopplung beachten</span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Systemtreue wahren</span>
              <span className="bg-white/10 px-4 py-2 rounded-full text-sm font-medium text-mk-teal-300">Q3 für Wohnräume</span>
            </div>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Benötigen Sie Profi-Unterstützung?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Keine Lust auf Staub und endloses Schleifen? Wir übernehmen Ihren <Link to="/trockenbau" className="text-mk-teal-600 font-bold">Trockenbau in Heinsberg</Link> – schnell, sauber und in garantierter Q3/Q4 Qualität.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Angebot anfordern
              </Link>
              <Link
                to="/trockenbau"
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Unsere Leistungen
              </Link>
            </div>
          </div>
        </motion.article>
      </div>

      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default DrywallGuide;